import { FIELD_AGGREGATORS, AGGREGATION_TYPES } from '@carto/kepler.gl/constants'
import {
  KeplerLayer,
  KeplerStoredLayer,
  KeplerVisualChannel,
  LayerPopupSettings,
  LayerType,
  LayerTypes,
  PopupEvent
} from '../types'
import {
  AGGREGATION_TYPES_TO_SQL,
  columnPlaceHolder,
  QUADBIN_RESOLUTION,
  H3_RESOLUTION,
  DEFAULT_AGGREGATION_EXP
} from '../ui/builderConstants'
import { SchemaField } from 'features/data-explorer/types'
import { ProviderType } from 'features/connections'

interface GetAggregationExp {
  layers: KeplerLayer[]
  popupSettings: Record<string, LayerPopupSettings>
  provider: ProviderType
  defaultDatasetAggregationExpressions: string[]
  validFields?: string[]
}

const getDefaultColumnFromSchemaForAggregationExp = (schema?: SchemaField[]) => {
  return schema ? schema[0].name : ''
}

export const getDefaultAggregationExpForLayerType = (
  layerType: LayerType,
  provider: ProviderType,
  schema?: SchemaField[]
) => {
  if (schema && layerType === LayerTypes.ClusterTile) {
    return getAggregationExp(getDefaultColumnFromSchemaForAggregationExp(schema), AGGREGATION_TYPES.count, provider)
  } else {
    return DEFAULT_AGGREGATION_EXP
  }
}

export function buildAggregationExp({
  layers,
  popupSettings,
  provider,
  defaultDatasetAggregationExpressions = [],
  validFields
}: GetAggregationExp): string | null {
  const visualChannelsWithAggregation = ['color', 'strokeColor', 'size', 'height', 'weight', 'radius']
  const aggregationExp: string[] = defaultDatasetAggregationExpressions

  for (const layer of layers) {
    const visualChannels = layer.visualChannels
    for (const visualChannelKey of visualChannelsWithAggregation) {
      const channel = visualChannels[visualChannelKey]
      if (channel) {
        aggregationExp.push(...getAggregationExpFromLayerChannel(layer, channel, provider, validFields))
      }
    }
  }
  aggregationExp.push(...getAggregationExpFromPopupSettings(popupSettings, provider, validFields))
  return [...new Set(aggregationExp.filter((exp) => exp))].join(',') || null
}

function getAggregationExpFromLayerChannel(
  layer: KeplerLayer | KeplerStoredLayer,
  channel: KeplerVisualChannel,
  provider: ProviderType,
  validFields?: string[]
): string[] {
  const aggregationExp: string[] = []
  const { aggregation, field } = channel
  if (!field || !aggregation) return []

  const fieldValue = layer.config[field]?.name
  const colorColumnField = layer.config[field]?.colorColumn
  const aggregationValue = layer.config.visConfig[aggregation]

  if (!isValidField(fieldValue, validFields)) {
    return aggregationExp
  }

  if (fieldValue && aggregationValue) {
    aggregationExp.push(getAggregationExp(fieldValue, aggregationValue, provider))
  }
  if (colorColumnField) {
    aggregationExp.push(getAggregationExp(colorColumnField, AGGREGATION_TYPES.mode, provider))
  }
  return aggregationExp
}

export function getAggregationExpFromPopupSettings(
  popupSettings: Record<string, LayerPopupSettings>,
  provider: ProviderType,
  validFields?: string[]
) {
  return Object.values(popupSettings).reduce((aggregationExp, settings) => {
    ;[...(settings[PopupEvent.Hover]?.fields || []), ...(settings[PopupEvent.Click]?.fields || [])].forEach((field) => {
      if (field.spatialIndexAggregation && isValidField(field.name, validFields)) {
        aggregationExp.push(getAggregationExp(field.name, field.spatialIndexAggregation, provider))
      }
    })
    return aggregationExp
  }, [] as string[])
}

function getAggregationExp(fieldValue: string, aggregationValue: string, provider: ProviderType) {
  const aggregationFunction = AGGREGATION_TYPES_TO_SQL[provider][aggregationValue]

  if (!aggregationFunction) {
    throw new Error(`Invalid aggregation ${aggregationValue}`)
  }
  return `${aggregationFunction.replace(columnPlaceHolder, fieldValue)} as ${getColumnAliasForAggregationExp(
    fieldValue,
    aggregationValue,
    provider
  )}`
}

function isValidField(field: string, validFields: string[] | undefined) {
  return !validFields || validFields.includes(field)
}

export function getColumnAliasForAggregationExp(name: string, aggregation: string, provider: ProviderType) {
  const columnAlias = `${name}_${aggregation}`
  return provider === ProviderType.SNOWFLAKE || provider === ProviderType.ORACLE
    ? columnAlias.toUpperCase()
    : columnAlias
}

export function getSchemaColumnFromColumnAlias(columnAlias: string) {
  for (const type of Object.values(AGGREGATION_TYPES) as string[]) {
    const typeWithSeparator = `_${type}`
    if (columnAlias.toLowerCase().endsWith(typeWithSeparator)) {
      return columnAlias.slice(0, -typeWithSeparator.length)
    }
  }
  return columnAlias
}

export function formatAggregatedField(field: string, aggregation: string) {
  const formatedAggregation = FIELD_AGGREGATORS.find((f) => f.id === aggregation)?.displayName || aggregation
  return formatedAggregation ? `${formatedAggregation}(${field})` : field
}

export function getDefaultAggregationResLevels(layerType: LayerType) {
  if (layerType === LayerTypes.Quadbin) {
    return { min: QUADBIN_RESOLUTION.min, default: QUADBIN_RESOLUTION.default, max: QUADBIN_RESOLUTION.max }
  }
  if (layerType === LayerTypes.H3) {
    return { min: H3_RESOLUTION.min, default: H3_RESOLUTION.default, max: H3_RESOLUTION.max }
  }
}
